/**
 * CrmModulePage – CRM Modul mit Tab-Navigation.
 * Tab 1: "Unternehmen" — Unternehmensliste mit CRUD.
 * Tab 2: "Kontakte" — Kontaktliste mit CRUD.
 * Tab 3: "Berechtigungen" — Modul-Berechtigungen verwalten (nur PLATFORM_ADMIN).
 */
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuthorization } from '../auth/useAuthorization';
import api from '../api/client';
import { UserAvatar } from '../components/UserAvatar';
import { CompaniesPage } from './companies/CompaniesPage';
import { ContactsPage } from './contacts/ContactsPage';
import { CrmSettingsTab, CrmMasterDataCards } from './CrmSettingsTab';
import { CpcAdminTab } from '../ctb/CpcAdminTab';
import styles from './CrmModulePage.module.css';

type CrmTab = 'companies' | 'contacts' | 'settings' | 'permissions' | 'master-data' | 'kalkulation';

interface UserInfo {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  jobTitle?: string | null;
  department?: string | null;
  avatar?: string | null;
}

interface Permission {
  id: string;
  key: string;
  name: string;
  description?: string;
  scope: string;
  moduleKey?: string;
}

interface PermissionGroup {
  id: string;
  name: string;
  description?: string;
  color?: string;
  isSystem: boolean;
  groupPermissions: { permission: Permission }[];
  userGroups?: { user: UserInfo }[];
  _count?: { userGroups: number };
}

// ── CRM Permission Labels ──────────────────────────────────────────────────

const PERM_LABELS: Record<string, { label: string; description: string }> = {
  'crm.view': {
    label: 'CRM anzeigen',
    description: 'Unternehmen und Kontakte anzeigen',
  },
  'crm.edit': {
    label: 'CRM bearbeiten',
    description: 'Unternehmen und Kontakte erstellen und bearbeiten',
  },
  'crm.delete': {
    label: 'CRM löschen',
    description: 'Unternehmen und Kontakte löschen',
  },
  'crm.export': {
    label: 'CRM exportieren',
    description: 'CRM-Daten exportieren (CSV, PDF)',
  },
  'crm.admin': {
    label: 'CRM Administrator',
    description: 'Vollzugriff auf alle CRM-Funktionen und Einstellungen',
  },
};

const GROUP_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

// ── Hauptkomponente ─────────────────────────────────────────────────────────

export function CrmModulePage() {
  const { isPlatformAdmin, canAdminModule } = useAuthorization();
  const canManagePermissions = isPlatformAdmin || canAdminModule('crm');

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');

  const [activeTab, setActiveTab] = useState<CrmTab>(
    tabParam === 'contacts' ? 'contacts' : tabParam === 'settings' ? 'settings' : tabParam === 'permissions' ? 'permissions' : 'companies',
  );

  // Tab-Wechsel via Sidebar-Links (Query-Parameter)
  useEffect(() => {
    if (tabParam === 'contacts') setActiveTab('contacts');
    else if (tabParam === 'settings') setActiveTab('settings');
    else if (tabParam === 'permissions') setActiveTab('permissions');
    else if (tabParam === 'master-data') setActiveTab('master-data');
    else if (tabParam === 'kalkulation') setActiveTab('kalkulation');
    else if (tabParam === 'companies' || !tabParam) setActiveTab('companies');
  }, [tabParam]);

  // Alle User laden (nur fuer Berechtigungen-Tab)
  const { data: users } = useQuery<UserInfo[]>({
    queryKey: ['users-list'],
    queryFn: async () => {
      const { data } = await api.get('/users', { params: { limit: 500 } });
      return data.data ?? data;
    },
    enabled: activeTab === 'permissions',
  });

  // RBAC: Gruppen laden (fuer Berechtigungen-Tab)
  const { data: groups, refetch: refetchGroups } = useQuery<PermissionGroup[]>({
    queryKey: ['admin', 'rbac', 'groups'],
    queryFn: async () => {
      const res = await api.get('/rbac/groups');
      return res.data;
    },
    enabled: activeTab === 'permissions' && canManagePermissions,
  });

  // RBAC: Modul-Permissions laden
  const { data: allPermissions, isLoading: permsLoading, error: permsError } = useQuery<Permission[]>({
    queryKey: ['admin', 'rbac', 'permissions', 'module', 'crm'],
    queryFn: async () => {
      const res = await api.get('/rbac/permissions', { params: { scope: 'MODULE' } });
      return res.data;
    },
    enabled: activeTab === 'permissions' && canManagePermissions,
  });

  // Nur CRM-Modul-Permissions
  const modulePermissions = (allPermissions ?? []).filter(
    (p) => p.moduleKey === 'crm',
  );

  const isAdminTab = activeTab === 'settings' || activeTab === 'permissions' || activeTab === 'master-data' || activeTab === 'kalkulation';

  return (
    <div>
      {/* Tab-Leiste — nur fuer CRM Admin Bereich (Einstellungen/Berechtigungen) */}
      {isAdminTab && canManagePermissions && (
        <div className={styles.tabBar}>
          <div className={styles.tabs}>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'master-data' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('master-data')}
            >
              Stammdaten
            </button>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'settings' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              Integrationen
            </button>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'permissions' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('permissions')}
            >
              Berechtigungen
            </button>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'kalkulation' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('kalkulation')}
            >
              Kalkulation
            </button>
          </div>
        </div>
      )}

      {/* Tab-Inhalt */}
      <div className={isAdminTab ? styles.tabContent : undefined}>
        {activeTab === 'companies' && <CompaniesPage />}
        {activeTab === 'contacts' && <ContactsPage />}

        {activeTab === 'settings' && canManagePermissions && (
          <CrmSettingsTab />
        )}

        {activeTab === 'master-data' && canManagePermissions && (
          <CrmMasterDataCards />
        )}

        {activeTab === 'kalkulation' && canManagePermissions && (
          <CpcAdminTab />
        )}

        {activeTab === 'permissions' && canManagePermissions && (
          <>
            {permsLoading ? (
              <p className={styles.loading}>Berechtigungen werden geladen...</p>
            ) : permsError ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#dc2626' }}>
                <p>Fehler beim Laden der Berechtigungen.</p>
                <p style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>{String(permsError)}</p>
              </div>
            ) : (
              <CrmPermissionsTab
                groups={groups ?? []}
                modulePermissions={modulePermissions}
                users={users ?? []}
                onRefresh={() => refetchGroups()}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Berechtigungen-Tab ──────────────────────────────────────────────────────

function CrmPermissionsTab({
  groups,
  modulePermissions,
  users,
  onRefresh,
}: {
  groups: PermissionGroup[];
  modulePermissions: Permission[];
  users: UserInfo[];
  onRefresh: () => void;
}) {
  const [editingGroup, setEditingGroup] = useState<PermissionGroup | null>(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showAddUsers, setShowAddUsers] = useState<string | null>(null);

  // Gruppen die mindestens eine CRM-Berechtigung haben
  const relevantGroups = groups.filter((g) =>
    g.groupPermissions.some((gp) => gp.permission.moduleKey === 'crm'),
  );

  // ── Mutations ──

  const createGroupMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string; color?: string; permissionIds: string[] }) => {
      const res = await api.post('/rbac/groups', data);
      return res.data;
    },
    onSuccess: () => {
      onRefresh();
      setShowCreateGroup(false);
    },
  });

  const updateGroupMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name?: string; description?: string; color?: string; permissionIds?: string[] } }) => {
      const res = await api.patch(`/rbac/groups/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      onRefresh();
      setEditingGroup(null);
    },
  });

  const deleteGroupMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/rbac/groups/${id}`);
    },
    onSuccess: () => {
      onRefresh();
    },
  });

  const addUserToGroupMutation = useMutation({
    mutationFn: async ({ groupId, userId }: { groupId: string; userId: string }) => {
      await api.post(`/rbac/groups/${groupId}/users`, { userId });
    },
    onSuccess: () => {
      onRefresh();
    },
  });

  const removeUserFromGroupMutation = useMutation({
    mutationFn: async ({ groupId, userId }: { groupId: string; userId: string }) => {
      await api.delete(`/rbac/groups/${groupId}/users/${userId}`);
    },
    onSuccess: () => {
      onRefresh();
    },
  });

  if (modulePermissions.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--color-text-muted)' }}>
        <p style={{ fontSize: '0.9375rem' }}>Keine CRM-Berechtigungen registriert.</p>
        <p style={{ fontSize: '0.8125rem', marginTop: '0.5rem' }}>
          Der CRM-Service muss die Berechtigungen beim Core-Service registrieren.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Aktions-Leiste */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
            CRM-Berechtigungsgruppen
          </h3>
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', margin: '0.25rem 0 0' }}>
            Verwalten Sie Gruppen und deren Berechtigungen fuer das CRM-Modul.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateGroup(true)}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            border: 'none',
            background: 'var(--color-primary, #3B82F6)',
            color: '#fff',
            fontSize: '0.8125rem',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          + Neue Gruppe
        </button>
      </div>

      {/* Gruppe erstellen Dialog */}
      {showCreateGroup && (
        <GroupForm
          modulePermissions={modulePermissions}
          onSave={(data) => createGroupMutation.mutate(data)}
          onCancel={() => setShowCreateGroup(false)}
          saving={createGroupMutation.isPending}
        />
      )}

      {/* Gruppe bearbeiten Dialog */}
      {editingGroup && (
        <GroupForm
          group={editingGroup}
          modulePermissions={modulePermissions}
          onSave={(data) => updateGroupMutation.mutate({ id: editingGroup.id, data })}
          onCancel={() => setEditingGroup(null)}
          saving={updateGroupMutation.isPending}
        />
      )}

      {/* Gruppen-Karten */}
      {relevantGroups.length === 0 && !showCreateGroup ? (
        <div style={{
          background: 'var(--color-bg-card)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-sm)',
          border: '1px solid var(--color-border)',
          padding: '2rem',
          textAlign: 'center',
          color: 'var(--color-text-muted)',
        }}>
          <p style={{ fontSize: '0.875rem' }}>
            Noch keine Gruppen mit CRM-Berechtigungen konfiguriert.
          </p>
          <p style={{ fontSize: '0.8125rem', marginTop: '0.25rem' }}>
            Erstellen Sie eine neue Gruppe, um Berechtigungen fuer das CRM-Modul zu vergeben.
          </p>
        </div>
      ) : (
        relevantGroups.map((group) => (
          <div
            key={group.id}
            style={{
              background: 'var(--color-bg-card)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-sm)',
              border: '1px solid var(--color-border)',
              overflow: 'hidden',
            }}
          >
            {/* Gruppen-Header */}
            <div style={{
              padding: '1rem 1.25rem',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-bg)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {group.color && (
                  <span style={{
                    width: 12, height: 12, borderRadius: '50%',
                    background: group.color, display: 'inline-block',
                  }} />
                )}
                <div>
                  <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>
                    {group.name}
                    {group.isSystem && (
                      <span style={{
                        marginLeft: '0.5rem', padding: '0.125rem 0.375rem',
                        borderRadius: '9999px', fontSize: '0.6875rem',
                        background: '#e0e7ff', color: '#3730a3',
                      }}>
                        System
                      </span>
                    )}
                  </h4>
                  {group.description && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.125rem 0 0' }}>
                      {group.description}
                    </p>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setEditingGroup(group)}
                  style={{
                    padding: '0.375rem 0.75rem', borderRadius: '0.25rem',
                    border: '1px solid var(--color-border)', background: 'var(--color-bg-card)',
                    fontSize: '0.75rem', cursor: 'pointer', color: 'var(--color-text)',
                  }}
                >
                  Bearbeiten
                </button>
                {!group.isSystem && (
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Gruppe "${group.name}" wirklich löschen?`)) {
                        deleteGroupMutation.mutate(group.id);
                      }
                    }}
                    style={{
                      padding: '0.375rem 0.75rem', borderRadius: '0.25rem',
                      border: '1px solid #fecaca', background: '#fef2f2',
                      fontSize: '0.75rem', cursor: 'pointer', color: '#dc2626',
                    }}
                  >
                    Löschen
                  </button>
                )}
              </div>
            </div>

            {/* Berechtigungen der Gruppe */}
            <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--color-border)' }}>
              <div style={{ fontSize: '0.6875rem', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: '0.375rem', fontWeight: 600 }}>
                Berechtigungen
              </div>
              <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                {modulePermissions.map((perm) => {
                  const hasPerm = group.groupPermissions.some(
                    (gp) => gp.permission.key === perm.key,
                  );
                  return (
                    <span
                      key={perm.id}
                      style={{
                        padding: '0.25rem 0.625rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        background: hasPerm ? '#dcfce7' : '#f3f4f6',
                        color: hasPerm ? '#166534' : '#9ca3af',
                        border: `1px solid ${hasPerm ? '#bbf7d0' : '#e5e7eb'}`,
                      }}
                    >
                      {hasPerm ? '✓ ' : ''}{PERM_LABELS[perm.key]?.label ?? perm.name}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Benutzer in der Gruppe */}
            <div style={{ padding: '0.75rem 1.25rem' }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: '0.5rem',
              }}>
                <div style={{ fontSize: '0.6875rem', textTransform: 'uppercase', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                  Mitglieder ({group._count?.userGroups ?? group.userGroups?.length ?? 0})
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddUsers(showAddUsers === group.id ? null : group.id)}
                  style={{
                    padding: '0.25rem 0.5rem', borderRadius: '0.25rem',
                    border: '1px solid var(--color-border)', background: 'var(--color-bg-card)',
                    fontSize: '0.6875rem', cursor: 'pointer', color: 'var(--color-text)',
                  }}
                >
                  + Benutzer hinzufügen
                </button>
              </div>

              {/* User hinzufügen Dropdown */}
              {showAddUsers === group.id && (
                <AddUserDropdown
                  users={users}
                  existingUserIds={new Set((group.userGroups ?? []).map((ug) => ug.user.id))}
                  onAdd={(userId) => addUserToGroupMutation.mutate({ groupId: group.id, userId })}
                  onClose={() => setShowAddUsers(null)}
                />
              )}

              {/* Bestehende Mitglieder */}
              {(group.userGroups ?? []).length === 0 ? (
                <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', fontStyle: 'italic', margin: '0.25rem 0' }}>
                  Keine Mitglieder in dieser Gruppe.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {(group.userGroups ?? []).map((ug) => (
                    <div
                      key={ug.user.id}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '0.375rem 0.5rem', borderRadius: '0.25rem',
                        background: 'var(--color-bg)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <UserAvatar
                          firstName={ug.user.firstName}
                          lastName={ug.user.lastName}
                          avatar={ug.user.avatar}
                          size={24}
                        />
                        <span style={{ fontSize: '0.8125rem' }}>
                          {ug.user.firstName} {ug.user.lastName}
                        </span>
                        <span style={{ fontSize: '0.6875rem', color: 'var(--color-text-muted)' }}>
                          {ug.user.email}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeUserFromGroupMutation.mutate({ groupId: group.id, userId: ug.user.id })}
                        style={{
                          padding: '0.125rem 0.375rem', borderRadius: '0.25rem',
                          border: 'none', background: 'transparent',
                          fontSize: '0.75rem', cursor: 'pointer', color: '#dc2626',
                        }}
                        title="Aus Gruppe entfernen"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))
      )}

      {/* Übersichts-Matrix */}
      {relevantGroups.length > 0 && (
        <div style={{
          background: 'var(--color-bg-card)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-sm)',
          border: '1px solid var(--color-border)',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '1rem 1.25rem',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg)',
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
              Berechtigungs-Matrix
            </h3>
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', margin: '0.25rem 0 0' }}>
              Übersicht aller Berechtigungen nach Gruppen.
            </p>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                <th style={{
                  padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem',
                  textTransform: 'uppercase', color: 'var(--color-text-muted)', whiteSpace: 'nowrap',
                }}>
                  Berechtigung
                </th>
                {relevantGroups.map((g) => (
                  <th key={g.id} style={{
                    padding: '0.75rem 0.75rem', textAlign: 'center', fontSize: '0.75rem',
                    textTransform: 'uppercase', color: 'var(--color-text-muted)', whiteSpace: 'nowrap',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem' }}>
                      {g.color && <span style={{ width: 8, height: 8, borderRadius: '50%', background: g.color, display: 'inline-block' }} />}
                      {g.name}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {modulePermissions.map((perm) => (
                <tr key={perm.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      {PERM_LABELS[perm.key]?.label ?? perm.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                      {PERM_LABELS[perm.key]?.description ?? perm.key}
                    </div>
                  </td>
                  {relevantGroups.map((group) => {
                    const hasPerm = group.groupPermissions.some(
                      (gp) => gp.permission.key === perm.key,
                    );
                    return (
                      <td key={group.id} style={{ padding: '0.75rem 0.75rem', textAlign: 'center' }}>
                        {hasPerm ? (
                          <span style={{
                            display: 'inline-flex', width: 22, height: 22,
                            alignItems: 'center', justifyContent: 'center',
                            borderRadius: '50%', background: '#dcfce7', color: '#166534',
                            fontSize: '0.8125rem', fontWeight: 700,
                          }}>
                            ✓
                          </span>
                        ) : (
                          <span style={{
                            display: 'inline-flex', width: 22, height: 22,
                            alignItems: 'center', justifyContent: 'center',
                            borderRadius: '50%', background: '#f3f4f6', color: '#9ca3af',
                            fontSize: '0.75rem',
                          }}>
                            —
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Gruppen-Formular (Create/Edit) ──────────────────────────────────────────

function GroupForm({
  group,
  modulePermissions,
  onSave,
  onCancel,
  saving,
}: {
  group?: PermissionGroup;
  modulePermissions: Permission[];
  onSave: (data: { name: string; description?: string; color?: string; permissionIds: string[] }) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [color, setColor] = useState(group?.color ?? GROUP_COLORS[0]);
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(
    new Set(group?.groupPermissions.map((gp) => gp.permission.id) ?? []),
  );

  const togglePerm = (id: string) => {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      description: description.trim() || undefined,
      color,
      permissionIds: Array.from(selectedPerms),
    });
  };

  return (
    <div style={{
      background: 'var(--color-bg-card)',
      borderRadius: 'var(--radius-md)',
      boxShadow: 'var(--shadow-md, var(--shadow-sm))',
      border: '1px solid var(--color-primary, #3B82F6)',
      padding: '1.25rem',
    }}>
      <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: '0 0 1rem' }}>
        {group ? `Gruppe "${group.name}" bearbeiten` : 'Neue CRM-Berechtigungsgruppe'}
      </h4>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {/* Name */}
        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>
            Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. CRM-Vertrieb"
            style={{
              width: '100%', padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem', border: '1px solid var(--color-border)',
              fontSize: '0.875rem', background: 'var(--color-bg)',
              color: 'var(--color-text)',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Beschreibung */}
        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>
            Beschreibung
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optionale Beschreibung"
            style={{
              width: '100%', padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem', border: '1px solid var(--color-border)',
              fontSize: '0.875rem', background: 'var(--color-bg)',
              color: 'var(--color-text)',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Farbe */}
        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>
            Farbe
          </label>
          <div style={{ display: 'flex', gap: '0.375rem' }}>
            {GROUP_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: c, border: color === c ? '2px solid var(--color-text)' : '2px solid transparent',
                  cursor: 'pointer', padding: 0,
                }}
              />
            ))}
          </div>
        </div>

        {/* Berechtigungen */}
        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.375rem' }}>
            CRM-Berechtigungen
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {modulePermissions.map((perm) => (
              <label
                key={perm.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                  border: '1px solid var(--color-border)',
                  background: selectedPerms.has(perm.id) ? '#f0fdf4' : 'var(--color-bg)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedPerms.has(perm.id)}
                  onChange={() => togglePerm(perm.id)}
                  style={{ accentColor: 'var(--color-primary, #3B82F6)' }}
                />
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    {PERM_LABELS[perm.key]?.label ?? perm.name}
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--color-text-muted)' }}>
                    {PERM_LABELS[perm.key]?.description ?? perm.key}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '0.5rem 1rem', borderRadius: '0.375rem',
              border: '1px solid var(--color-border)', background: 'var(--color-bg-card)',
              fontSize: '0.8125rem', cursor: 'pointer', color: 'var(--color-text)',
            }}
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !name.trim()}
            style={{
              padding: '0.5rem 1rem', borderRadius: '0.375rem',
              border: 'none', background: 'var(--color-primary, #3B82F6)',
              color: '#fff', fontSize: '0.8125rem', fontWeight: 500,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving || !name.trim() ? 0.6 : 1,
            }}
          >
            {saving ? 'Speichern...' : group ? 'Aktualisieren' : 'Erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Benutzer-hinzufügen Dropdown ───────────────────────────────────────────

function AddUserDropdown({
  users,
  existingUserIds,
  onAdd,
  onClose,
}: {
  users: UserInfo[];
  existingUserIds: Set<string>;
  onAdd: (userId: string) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');

  const available = users.filter((u) => {
    if (existingUserIds.has(u.id)) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      u.firstName.toLowerCase().includes(q) ||
      u.lastName.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q)
    );
  });

  return (
    <div style={{
      marginBottom: '0.75rem', padding: '0.75rem',
      borderRadius: '0.375rem', border: '1px solid var(--color-border)',
      background: 'var(--color-bg)',
    }}>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Benutzer suchen..."
        style={{
          width: '100%', padding: '0.375rem 0.625rem',
          borderRadius: '0.25rem', border: '1px solid var(--color-border)',
          fontSize: '0.8125rem', marginBottom: '0.5rem',
          background: 'var(--color-bg-card)', color: 'var(--color-text)',
          boxSizing: 'border-box',
        }}
        autoFocus
      />
      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
        {available.length === 0 ? (
          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', textAlign: 'center', padding: '0.5rem' }}>
            {search ? 'Kein Benutzer gefunden.' : 'Alle Benutzer sind bereits Mitglied.'}
          </p>
        ) : (
          available.slice(0, 20).map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => onAdd(u.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                width: '100%', padding: '0.375rem 0.5rem',
                borderRadius: '0.25rem', border: 'none',
                background: 'transparent', cursor: 'pointer',
                textAlign: 'left', fontSize: '0.8125rem',
                color: 'var(--color-text)',
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = 'var(--color-bg-card)')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <UserAvatar firstName={u.firstName} lastName={u.lastName} avatar={u.avatar} size={22} />
              <span>{u.firstName} {u.lastName}</span>
              <span style={{ fontSize: '0.6875rem', color: 'var(--color-text-muted)' }}>{u.email}</span>
            </button>
          ))
        )}
      </div>
      <div style={{ textAlign: 'right', marginTop: '0.375rem' }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: '0.25rem 0.5rem', borderRadius: '0.25rem',
            border: '1px solid var(--color-border)', background: 'var(--color-bg-card)',
            fontSize: '0.6875rem', cursor: 'pointer', color: 'var(--color-text)',
          }}
        >
          Schließen
        </button>
      </div>
    </div>
  );
}
